import type { NewsSource } from "../models/news.js";
import type { StationV2 } from "../models/station.js";

/**
 * Browsing helpers for the TV / Radio lists: a small fixed category set
 * derived from free-form `category` / `tags`, region grouping, distance
 * sorting, and health-aware ordering. Pure functions — no React, no I/O.
 */

export const CATEGORY_IDS = [
  "news",
  "sports",
  "music",
  "entertainment",
  "kids",
  "culture",
  "religious",
  "talk",
  "general",
] as const;
export type CategoryId = (typeof CATEGORY_IDS)[number];

// Lowercased, accent-stripped tokens → category. iptv-org categories are
// a closed list; radio-browser tags are free text in many languages, so the
// music bucket is mostly genre names.
const TOKEN_TO_CATEGORY: Record<string, CategoryId> = {
  news: "news", noticias: "news", noticia: "news", informativo: "news", informativa: "news", weather: "news",
  business: "news", legislative: "news", information: "news", informacion: "news", economics: "news",
  traffic: "news", "local news": "news", "新闻": "news", "资讯": "news", "财经": "news", "財經": "news",
  sports: "sports", sport: "sports", deportes: "sports", deportiva: "sports", deportivo: "sports", futbol: "sports",
  football: "sports", soccer: "sports", outdoor: "sports", auto: "sports", "体育": "sports",
  music: "music", musica: "music", musical: "music", pop: "music", rock: "music", salsa: "music", jazz: "music",
  classical: "music", clasica: "music", latin: "music", latina: "music", tropical: "music", reggae: "music",
  reggaeton: "music", romantica: "music", romantic: "music", baladas: "music", ballads: "music", hits: "music",
  dance: "music", electronic: "music", oldies: "music", "80s": "music", "90s": "music", top40: "music",
  merengue: "music", bachata: "music", cumbia: "music", blues: "music", country: "music", "hip-hop": "music",
  rap: "music", chill: "music", lounge: "music", relax: "music", instrumental: "music", folk: "music",
  urbano: "music", urbana: "music", "classic rock": "music", "classic hits": "music", "pop rock": "music",
  "pop music": "music", "musica romantica": "music", "音乐": "music", "流行": "music",
  entertainment: "entertainment", comedy: "entertainment", series: "entertainment", movies: "entertainment",
  lifestyle: "entertainment", cooking: "entertainment", shopping: "entertainment", entretenimiento: "entertainment",
  "综艺": "entertainment", "电影": "entertainment", "电视剧": "entertainment",
  kids: "kids", children: "kids", infantil: "kids", ninos: "kids", animation: "kids", family: "kids", "少儿": "kids",
  culture: "culture", cultura: "culture", cultural: "culture", documentary: "culture", education: "culture",
  educativa: "culture", educacion: "culture", science: "culture", travel: "culture", classic: "culture",
  university: "culture", universidad: "culture", universitaria: "culture", educational: "culture",
  storytelling: "culture", literature: "culture", "纪录": "culture", "教育": "culture",
  religious: "religious", religion: "religious", christian: "religious", cristiana: "religious",
  cristiano: "religious", catolica: "religious", catholic: "religious", evangelica: "religious",
  evangelical: "religious", gospel: "religious", worship: "religious", adventista: "religious",
  talk: "talk", hablada: "talk", conversacion: "talk", opinion: "talk", podcast: "talk", community: "talk",
  comunitaria: "talk", "talk & speech": "talk",
  general: "general", variety: "general", variedades: "general", "综合": "general",
};

// Single words too generic (or misleading) to classify a multi-word tag on
// their own — "classic rock" is music, not the iptv-org "classic" (films).
const WORD_STOPLIST = new Set(["classic", "radio", "fm", "am", "tv", "estacion", "station", "costa", "rica"]);

function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/** Every category a station belongs to (deduped, in first-match order). */
export function stationCategories(s: Pick<StationV2, "category" | "tags">): CategoryId[] {
  const raw = [s.category, ...(s.tags ?? [])].filter((x): x is string => typeof x === "string" && x.length > 0);
  const out: CategoryId[] = [];
  for (const r of raw) {
    const folded = fold(r);
    let hit = TOKEN_TO_CATEGORY[folded] ?? TOKEN_TO_CATEGORY[folded.replace(/\s+/g, "")];
    if (!hit) {
      // Multi-word free-text tags ("música y noticias", "baladas en español"):
      // classify by their first recognizable word.
      for (const w of folded.split(/[\s/&,-]+/)) {
        if (WORD_STOPLIST.has(w)) continue;
        hit = TOKEN_TO_CATEGORY[w];
        if (hit) break;
      }
    }
    if (hit && !out.includes(hit)) out.push(hit);
  }
  return out;
}

/** Categories present in a list, most common first (ties keep CATEGORY_IDS order). */
export function categoryCounts(list: readonly StationV2[]): Array<{ id: CategoryId; count: number }> {
  const counts = new Map<CategoryId, number>();
  for (const s of list) for (const c of stationCategories(s)) counts.set(c, (counts.get(c) ?? 0) + 1);
  return CATEGORY_IDS.filter((id) => counts.has(id))
    .map((id) => ({ id, count: counts.get(id)! }))
    .sort((a, b) => b.count - a.count);
}

/** Regions present in a list, most stations first. */
export function regionCounts(list: readonly StationV2[]): Array<{ region: string; count: number }> {
  const counts = new Map<string, { region: string; count: number }>();
  for (const s of list) {
    if (!s.region) continue;
    const key = fold(s.region);
    const cur = counts.get(key);
    if (cur) cur.count++;
    else counts.set(key, { region: s.region, count: 1 });
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.region.localeCompare(b.region));
}

export function sameRegion(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && fold(a) === fold(b);
}

/** Great-circle distance in km. */
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Stations with coordinates, nearest first, within `maxKm`. */
export function nearest<T extends StationV2>(list: readonly T[], from: { lat: number; lng: number }, maxKm = 150): T[] {
  return list
    .filter((s) => s.geo)
    .map((s) => ({ s, d: distanceKm(from, s.geo!) }))
    .filter(({ d }) => d <= maxKm)
    .sort((a, b) => a.d - b.d)
    .map(({ s }) => s);
}

/**
 * Whether the station looked playable at its last check. `live: false` is
 * authoritative; a health record with zero working urls also counts.
 */
export function isPlayable(s: Pick<StationV2, "live" | "health">): boolean {
  if (!s.live) return false;
  return !(s.health && s.health.okUrls === 0);
}

/** Playable stations first, keeping the source order (votes / iptv-org order) within each group. */
export function playableFirst<T extends StationV2>(list: readonly T[]): T[] {
  return [...list.filter(isPlayable), ...list.filter((s) => !isPlayable(s))];
}

export interface BrowseFilter {
  category?: CategoryId;
  region?: string;
}

export function filterStations<T extends StationV2>(list: readonly T[], f: BrowseFilter): T[] {
  return list.filter(
    (s) =>
      (!f.category || stationCategories(s).includes(f.category)) &&
      (!f.region || sameRegion(s.region, f.region))
  );
}

function matchKey(s: string): string {
  return fold(s).replace(/[^\p{L}\p{N}]+/gu, "");
}

/**
 * The live TV channel / radio station that belongs to a news outlet, for a
 * "Watch live" link on its stories. Uses the source's `liveChannel` when
 * configured, else the outlet name before any " — Section" suffix. Keys
 * shorter than 4 characters never match (too ambiguous). TV wins over radio;
 * only playable stations count.
 */
export function liveStationForSource(
  sourceName: string | undefined,
  sources: readonly NewsSource[],
  tv: readonly StationV2[],
  radio: readonly StationV2[]
): { kind: "tv" | "radio"; station: StationV2 } | undefined {
  if (!sourceName) return undefined;
  const source = sources.find((s) => s.name === sourceName || s.id === sourceName);
  const wanted = source?.liveChannel ?? sourceName.split(/\s[—–-]\s|:|\(/)[0] ?? sourceName;
  const key = matchKey(wanted);
  if (key.length < 4) return undefined;
  const find = (list: readonly StationV2[]) =>
    list.find((s) => isPlayable(s) && (s.id === wanted || matchKey(s.name).startsWith(key)));
  const t = find(tv);
  if (t) return { kind: "tv", station: t };
  const r = find(radio);
  return r ? { kind: "radio", station: r } : undefined;
}
